import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { Icon } from '../ui/Icon';
import { Bouton, Feuille, IconeBouton, Segmente, cn } from '../ui/kit';
import { Media } from '../ui/Media';
import { useBoutique } from '../state/boutique';
import { REGLAGES, BOUTIQUES } from '../data/mock-data';
import { eur, familles, rechercher, eurSansCentimes, boutique as trouverBoutique } from '../data/repository';

/**
 * EN-TÊTE — trois zones, deux lignes, une seule action forte.
 *
 * Mobile : menu | logo | panier. Rien d'autre. La recherche est une icône qui
 * ouvre une feuille plein écran ; le mode de service vit dans la feuille de menu.
 * Desktop : logo | navigation | recherche | compte | panier.
 *
 * Une seule barre d'annonce, statique — un texte qui défile ne se lit pas.
 */
const NAV = [
  { vers: '/boutique', libelle: 'La boutique' },
  { vers: '/notre-maison', libelle: 'Notre maison' },
  { vers: '/nos-boutiques', libelle: 'Nos boutiques' },
  { vers: '/livraison-et-retrait', libelle: 'Livraison & retrait' },
];

export function Logo({ centre }: { centre?: boolean }) {
  return (
    <Link to="/" aria-label="Maison Duval — accueil"
      className={cn('group flex shrink-0 flex-col leading-none', centre && 'items-center')}>
      <span className="font-display text-[1.35rem] tracking-tight text-encre-900 transition-colors group-hover:text-wine sm:text-[1.55rem]">
        Maison Duval
      </span>
      <span className="mt-1 text-[0.5625rem] font-semibold uppercase tracking-[0.24em] text-encre-500 sm:text-[0.625rem]">
        Boucherie · depuis 1922
      </span>
    </Link>
  );
}

export function Header() {
  const { nbArticles, total, mode, boutiqueId, ouvrirPanier, favoris, confort, basculerConfort } = useBoutique();
  const [menu, setMenu] = useState(false);
  const [recherche, setRecherche] = useState(false);
  const [modeOuvert, setModeOuvert] = useState(false);
  const [compact, setCompact] = useState(false);
  const boutique = trouverBoutique(boutiqueId);

  useEffect(() => {
    const surDefilement = () => setCompact(window.scrollY > 8);
    surDefilement();
    window.addEventListener('scroll', surDefilement, { passive: true });
    return () => window.removeEventListener('scroll', surDefilement);
  }, []);

  return (
    <>
      {/* Bandeau d'annonce — une phrase utile, pas un carrousel. */}
      <div className="bg-encre-900 text-lin-50">
        <div className="mx-auto flex h-8 max-w-[1280px] items-center justify-between gap-4 px-4 text-[0.6875rem] sm:h-9 sm:px-6 sm:text-xs">
          <p className="truncate">
            Livraison réfrigérée offerte dès {eurSansCentimes(REGLAGES.livraison.seuilGratuite)}
            <span className="mx-2 text-lin-50/40" aria-hidden="true">·</span>
            <span className="hidden sm:inline">Retrait gratuit en boutique sous 24 h</span>
            <span className="sm:hidden">Retrait gratuit sous 24 h</span>
          </p>
          <a href={`tel:${REGLAGES.marque.telephone.replace(/\s/g, '')}`}
            className="hidden shrink-0 items-center gap-1.5 text-lin-50/85 transition hover:text-lin-50 sm:flex">
            <Icon name="phone" size={13} /> {REGLAGES.marque.telephone}
          </a>
        </div>
      </div>

      <header className={cn('sticky top-0 z-40 border-b bg-lin-50/92 backdrop-blur-md transition-colors',
        compact ? 'border-lin-200' : 'border-transparent')}>

        {/* ---------- barre principale : mobile ---------- */}
        <div className="mx-auto flex h-16 max-w-[1280px] items-center gap-2 px-3 lg:hidden">
          <IconeBouton nom="menu" libelle="Ouvrir le menu" onClick={() => setMenu(true)} />
          <div className="flex flex-1 justify-center"><Logo centre /></div>
          <IconeBouton nom="search" libelle="Rechercher" onClick={() => setRecherche(true)} />
          <button type="button" onClick={() => ouvrirPanier(true)}
            className="relative grid h-11 w-11 place-items-center rounded-full text-encre-900"
            aria-label={`Panier, ${nbArticles} article${nbArticles !== 1 ? 's' : ''}`}>
            <Icon name="cart" size={22} />
            {nbArticles > 0 && (
              <span className="absolute -right-0.5 -top-0.5 grid h-5 min-w-5 place-items-center rounded-full bg-wine px-1 text-[0.625rem] font-bold text-white">
                {nbArticles}
              </span>
            )}
          </button>
        </div>

        {/* ---------- barre principale : desktop ---------- */}
        <div className="mx-auto hidden h-[76px] max-w-[1280px] items-center gap-8 px-6 lg:flex">
          <Logo />
          <nav aria-label="Navigation principale" className="flex items-center gap-1">
            {NAV.map(lien => (
              <NavLink key={lien.vers} to={lien.vers}
                className={({ isActive }) => cn(
                  'relative rounded-full px-3.5 py-2 text-[0.9375rem] font-medium transition-colors',
                  isActive ? 'text-wine' : 'text-encre-700 hover:text-encre-900',
                )}>
                {({ isActive }) => (
                  <>
                    {lien.libelle}
                    <span className={cn('absolute inset-x-3.5 -bottom-0.5 h-0.5 rounded-full bg-wine transition-opacity',
                      isActive ? 'opacity-100' : 'opacity-0')} aria-hidden="true" />
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="ml-auto flex items-center gap-1.5">
            <RechercheDesktop />
            <button type="button" onClick={() => setModeOuvert(true)}
              className="flex h-10 items-center gap-2 rounded-full bg-lin-100 px-3.5 text-sm font-medium text-encre-700 transition hover:bg-lin-200">
              <Icon name={mode === 'PICKUP' ? 'store' : 'truck'} size={17} />
              <span>{mode === 'PICKUP' ? `Retrait · ${boutique.nom}` : 'Livraison'}</span>
              <Icon name="chevronDown" size={14} />
            </button>
            <button type="button" onClick={basculerConfort} aria-pressed={confort}
              className={cn('grid h-10 w-10 place-items-center rounded-full text-[0.8125rem] font-semibold transition',
                confort ? 'bg-ink text-lin-50' : 'text-encre-600 hover:bg-lin-100 hover:text-encre-900')}
              title="Confort de lecture (+15 %)"
              aria-label={`Confort de lecture ${confort ? 'activé' : 'désactivé'}`}>
              Aa
            </button>
            <Link to="/mon-compte?vue=favoris" className="icon-btn relative" aria-label={`Mes favoris${favoris.length ? `, ${favoris.length}` : ''}`}>
              <Icon name="heart" size={20} filled={favoris.length > 0} />
              {favoris.length > 0 && (
                <span className="absolute right-1 top-1 grid h-4 min-w-4 place-items-center rounded-full bg-wine px-1 text-[0.5625rem] font-bold text-white">
                  {favoris.length}
                </span>
              )}
            </Link>
            <Link to="/mon-compte" className="icon-btn" aria-label="Mon compte">
              <Icon name="user" size={21} />
            </Link>
            <button type="button" onClick={() => ouvrirPanier(true)}
              className="flex h-11 items-center gap-2.5 rounded-full bg-encre-900 pl-4 pr-2 text-lin-50 transition hover:bg-encre-700"
              aria-label={`Voir mon panier, ${nbArticles} article${nbArticles !== 1 ? 's' : ''}${total > 0 ? `, ${eur(total)}` : ''}`}>
              <Icon name="cart" size={19} />
              <span className="text-sm font-semibold tabular-nums">{nbArticles > 0 ? eur(total) : 'Panier'}</span>
              <span className="grid h-7 min-w-7 place-items-center rounded-full bg-lin-50/15 px-1 text-xs font-bold">
                {nbArticles}
              </span>
            </button>
          </div>
        </div>

        {/* ---------- rail des familles : desktop ---------- */}
        <div className="hidden border-t border-lin-200/70 lg:block">
          <div className="mx-auto flex h-11 max-w-[1280px] items-center gap-1 overflow-x-auto px-6 no-scrollbar">
            <span className="mr-2 shrink-0 text-[0.6875rem] font-semibold uppercase tracking-[0.14em] text-encre-500">
              Le comptoir
            </span>
            {familles().map(f => (
              <NavLink key={f.slug} to={`/boutique/${f.slug}`}
                className={({ isActive }) => cn(
                  'shrink-0 rounded-full px-3 py-1.5 text-[0.8125rem] transition-colors',
                  isActive ? 'bg-lin-100 font-semibold text-encre-900' : 'text-encre-600 hover:bg-lin-100 hover:text-encre-900',
                )}>
                {f.nom}
              </NavLink>
            ))}
          </div>
        </div>
      </header>

      <MenuMobile ouvert={menu} fermer={() => setMenu(false)} />
      <RechercheGlobale ouverte={recherche} fermer={() => setRecherche(false)} />
      <PanneauMode ouvert={modeOuvert} fermer={() => setModeOuvert(false)} />
    </>
  );
}

/* --------------------------------------------------------------- RECHERCHE */

function RechercheDesktop() {
  const [ouvert, setOuvert] = useState(false);
  return (
    <>
      <button type="button" onClick={() => setOuvert(true)}
        className="hidden h-10 items-center gap-2.5 rounded-full bg-lin-100 px-3.5 text-sm text-encre-500 transition hover:bg-lin-200 hover:text-encre-700 xl:flex">
        <Icon name="search" size={17} />
        Rechercher une pièce
        <kbd className="ml-6 rounded border border-lin-200 bg-surface px-1.5 py-0.5 text-[0.625rem] text-encre-500">⌘K</kbd>
      </button>
      <IconeBouton nom="search" libelle="Rechercher" onClick={() => setOuvert(true)} className="xl:hidden" />
      <RechercheGlobale ouverte={ouvert} fermer={() => setOuvert(false)} discret />
    </>
  );
}

function RechercheGlobale({ ouverte, fermer, discret }: { ouverte: boolean; fermer: () => void; discret?: boolean }) {
  const [terme, setTerme] = useState('');
  const champ = useRef<HTMLInputElement>(null);
  const naviguer = useNavigate();
  const resultats = useMemo(() => rechercher(terme).slice(0, 6), [terme]);

  useEffect(() => {
    if (ouverte) window.setTimeout(() => champ.current?.focus(), 60);
    else setTerme('');
  }, [ouverte]);

  /* ⌘K / Ctrl+K : la recherche s'ouvre au clavier, depuis n'importe où. */
  useEffect(() => {
    const surTouche = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        if (!ouverte) document.querySelector<HTMLButtonElement>('[aria-label="Rechercher"]')?.click();
      }
    };
    window.addEventListener('keydown', surTouche);
    return () => window.removeEventListener('keydown', surTouche);
  }, [ouverte]);

  const aller = (slug: string) => {
    fermer();
    naviguer(`/produit/${slug}`);
  };

  return (
    <Feuille ouverte={ouverte} onFermer={fermer} titre="Rechercher"
      sousTitre="Une pièce, une race, un label" cote={discret ? 'right' : 'full'}>
      <div className="relative">
        <Icon name="search" size={19} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-encre-500" />
        <input ref={champ} type="search" value={terme} onChange={e => setTerme(e.target.value)}
          placeholder="Côte de bœuf, agneau, merguez…"
          className="input h-14 pl-12 text-base" aria-label="Rechercher un produit" />
      </div>

      {terme.trim().length >= 2 ? (
        resultats.length > 0 ? (
          <ul className="mt-4 divide-y divide-lin-200">
            {resultats.map(p => (
              <li key={p.id}>
                <button type="button" onClick={() => aller(p.slug)}
                  className="flex w-full items-center gap-3 py-3 text-left transition hover:opacity-80">
                  <Media src={p.photos[0]?.url} alt={p.nom} ratio="aspect-square" className="h-14 w-14 shrink-0 rounded-xl" />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[0.9375rem] font-medium text-encre-900">{p.nom}</span>
                    <span className="block text-xs text-encre-500">{p.familleNom} · {p.origine}</span>
                  </span>
                  <Icon name="chevronRight" size={16} className="shrink-0 text-encre-300" />
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="mt-6 text-sm text-encre-500">
            Rien ne porte ce nom. Essayez « bœuf », « agneau » ou « merguez » — ou appelez le comptoir,
            on vous dit ce qui est arrivé ce matin.
          </p>
        )
      ) : (
        <div className="mt-6">
          <p className="eyebrow">Recherches fréquentes</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {['Côte de bœuf', 'Agneau', 'Merguez', 'Volaille', 'Méchoui', 'Maturation'].map(t => (
              <button key={t} type="button" onClick={() => setTerme(t)}
                className="chip">
                {t}
              </button>
            ))}
          </div>
        </div>
      )}
    </Feuille>
  );
}

/* ------------------------------------------------------------- MENU MOBILE */

function MenuMobile({ ouvert, fermer }: { ouvert: boolean; fermer: () => void }) {
  const { mode, setMode, boutiqueId, setBoutique, favoris } = useBoutique();
  const boutique = trouverBoutique(boutiqueId);

  return (
    <Feuille ouverte={ouvert} onFermer={fermer} titre="Menu" cote="right">
      <nav aria-label="Navigation mobile">
        <ul className="space-y-1">
          {NAV.map(lien => (
            <li key={lien.vers}>
              <Link to={lien.vers} onClick={fermer}
                className="flex items-center justify-between rounded-xl px-3 py-3.5 text-[1.0625rem] font-medium text-encre-900 transition hover:bg-lin-100">
                {lien.libelle}
                <Icon name="chevronRight" size={17} className="text-encre-300" />
              </Link>
            </li>
          ))}
          <li>
            <Link to="/mon-compte" onClick={fermer}
              className="flex items-center justify-between rounded-xl px-3 py-3.5 text-[1.0625rem] font-medium text-encre-900 transition hover:bg-lin-100">
              Mon compte
              <Icon name="chevronRight" size={17} className="text-encre-300" />
            </Link>
          </li>
          <li>
            <Link to="/mon-compte?vue=favoris" onClick={fermer}
              className="flex items-center justify-between rounded-xl px-3 py-3.5 text-[1.0625rem] font-medium text-encre-900 transition hover:bg-lin-100">
              Mes favoris{favoris.length > 0 ? ` (${favoris.length})` : ''}
              <Icon name="heart" size={17} className="text-encre-300" />
            </Link>
          </li>
        </ul>
      </nav>

      <div className="mt-6 border-t border-lin-200 pt-5">
        <p className="eyebrow">Comment souhaitez-vous être servi ?</p>
        <div className="mt-3">
          <Segmente bloc libelle="Mode de service" valeur={mode}
            onChange={v => setMode(v as 'PICKUP' | 'DELIVERY')}
            options={[{ valeur: 'PICKUP', libelle: 'Retrait', icone: 'store' },
                      { valeur: 'DELIVERY', libelle: 'Livraison', icone: 'truck' }]} />
        </div>
        {mode === 'PICKUP' ? (
          <label className="mt-3 block">
            <span className="mb-1.5 block text-xs font-medium text-encre-500">Boutique de retrait</span>
            <select value={boutiqueId} onChange={e => setBoutique(e.target.value)} className="select">
              {BOUTIQUES.map(b => <option key={b.id} value={b.id}>{b.nom} — {b.adresse}</option>)}
            </select>
          </label>
        ) : (
          <p className="mt-3 rounded-xl bg-lin-100 px-3.5 py-3 text-[0.8125rem] leading-relaxed text-encre-600">
            Colis isotherme 0–4 °C, offert dès {eurSansCentimes(REGLAGES.livraison.seuilGratuite)}.
            Départ le jour ouvré suivant une commande passée avant {REGLAGES.livraison.coupure}.
          </p>
        )}
      </div>

      <div className="mt-6 border-t border-lin-200 pt-5">
        <p className="eyebrow">Les rayons</p>
        <ul className="mt-3 grid grid-cols-2 gap-x-4">
          {familles().map(f => (
            <li key={f.slug}>
              <Link to={`/boutique/${f.slug}`} onClick={fermer}
                className="flex items-baseline justify-between gap-2 py-2 text-[0.9375rem] text-encre-700">
                <span className="truncate">{f.nom}</span>
                <span className="shrink-0 text-xs text-encre-500">{f.nombre}</span>
              </Link>
            </li>
          ))}
        </ul>
      </div>

      <div className="mt-6 rounded-2xl bg-lin-100 p-4">
        <p className="text-sm font-semibold text-encre-900">Une question avant de commander ?</p>
        <p className="mt-1 text-[0.8125rem] text-encre-500">{boutique.nom} · {boutique.horaires.find(h => h.heures !== 'Fermé')?.heures}</p>
        <a href={`tel:${boutique.telephone.replace(/\s/g, '')}`} className="mt-3 btn btn--outline btn--sm btn--block">
          <Icon name="phone" size={16} /> Appeler le comptoir
        </a>
      </div>
    </Feuille>
  );
}

/* -------------------------------------------------------------- MODE PANEL */

function PanneauMode({ ouvert, fermer }: { ouvert: boolean; fermer: () => void }) {
  const { mode, setMode, boutiqueId, setBoutique } = useBoutique();
  return (
    <Feuille ouverte={ouvert} onFermer={fermer} titre="Retrait ou livraison"
      sousTitre="Le mode s'applique à tout le panier" cote="right">
      <Segmente bloc libelle="Mode de service" valeur={mode}
        onChange={v => setMode(v as 'PICKUP' | 'DELIVERY')}
        options={[{ valeur: 'PICKUP', libelle: 'Retrait en boutique', icone: 'store' },
                  { valeur: 'DELIVERY', libelle: 'Livraison réfrigérée', icone: 'truck' }]} />

      {mode === 'PICKUP' ? (
        <ul className="mt-4 space-y-2">
          {BOUTIQUES.map(b => (
            <li key={b.id}>
              <button type="button" onClick={() => setBoutique(b.id)}
                className={cn('w-full rounded-xl border p-4 text-left transition',
                  boutiqueId === b.id ? 'border-wine bg-wine-wash' : 'border-lin-200 bg-surface hover:border-laiton-300')}>
                <span className="flex items-center justify-between gap-2">
                  <span className="font-semibold text-encre-900">{b.nom}</span>
                  {boutiqueId === b.id && <Icon name="check" size={17} className="text-wine" />}
                </span>
                <span className="mt-1 block text-[0.8125rem] text-encre-500">{b.adresse}</span>
                <span className="mt-1 block text-xs text-encre-500">
                  {b.halalOnly ? 'Rayon halal · sans canard' : 'Charcuterie maison au comptoir'}
                </span>
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <div className="mt-4 space-y-3 rounded-xl bg-lin-100 p-4 text-[0.875rem] leading-relaxed text-encre-600">
          <p><strong className="text-encre-900">Colis isotherme 0–4 °C</strong>, expédié du mardi au samedi.</p>
          <p>Offert dès {eurSansCentimes(REGLAGES.livraison.seuilGratuite)}, sinon {eur(REGLAGES.livraison.fraisChronofresh)}.</p>
          <p>Commande avant {REGLAGES.livraison.coupure} pour un départ le jour ouvré suivant.</p>
        </div>
      )}

      <Bouton bloc className="mt-5" onClick={fermer}>Continuer</Bouton>
    </Feuille>
  );
}
